import * as SunCalc from 'suncalc';

export type MoonPhaseName =
  | 'new'
  | 'waxing_crescent'
  | 'first_quarter'
  | 'waxing_gibbous'
  | 'full'
  | 'waning_gibbous'
  | 'last_quarter'
  | 'waning_crescent';

export interface SunTimes {
  sunrise: Date;
  sunset: Date;
  civilDawn: Date;   // civil twilight start — "first light"
  civilDusk: Date;   // civil twilight end — "last light"
  solarNoon: Date;
}

export interface MoonData {
  phase: MoonPhaseName;
  illumination: number;      // 0-100 percentage
  phaseValue: number;        // 0-1 raw SunCalc value (0 = new, 0.5 = full)
  rise: Date | null;         // null if moon always up/down that day
  set: Date | null;          // null if moon always up/down that day
}

export interface CelestialData {
  sun: SunTimes;
  moon: MoonData;
}

// Phase boundaries: 8 equal segments of width 0.125, centered at multiples of 0.125
const PHASE_BOUNDARIES: { max: number; name: MoonPhaseName }[] = [
  { max: 0.0625, name: 'new' },
  { max: 0.1875, name: 'waxing_crescent' },
  { max: 0.3125, name: 'first_quarter' },
  { max: 0.4375, name: 'waxing_gibbous' },
  { max: 0.5625, name: 'full' },
  { max: 0.6875, name: 'waning_gibbous' },
  { max: 0.8125, name: 'last_quarter' },
  { max: 0.9375, name: 'waning_crescent' },
];

const PHASE_EMOJI: Record<MoonPhaseName, string> = {
  new: '🌑',
  waxing_crescent: '🌒',
  first_quarter: '🌓',
  waxing_gibbous: '🌔',
  full: '🌕',
  waning_gibbous: '🌖',
  last_quarter: '🌗',
  waning_crescent: '🌘',
};

export function getMoonPhaseName(phaseValue: number): MoonPhaseName {
  for (const boundary of PHASE_BOUNDARIES) {
    if (phaseValue < boundary.max) return boundary.name;
  }
  // Values >= 0.9375 wrap back to new
  return 'new';
}

export function getMoonPhaseEmoji(phase: MoonPhaseName): string {
  return PHASE_EMOJI[phase];
}

export function getSunTimes(date: Date, lat: number, lng: number): SunTimes {
  const times = SunCalc.getTimes(date, lat, lng);
  return {
    sunrise: times.sunrise,
    sunset: times.sunset,
    civilDawn: times.dawn,
    civilDusk: times.dusk,
    solarNoon: times.solarNoon,
  };
}

export function getMoonData(date: Date, lat: number, lng: number): MoonData {
  const illum = SunCalc.getMoonIllumination(date);
  const moonTimes = SunCalc.getMoonTimes(date, lat, lng);

  const phaseValue = illum.phase;
  const phase = getMoonPhaseName(phaseValue);
  const illumination = Math.round(illum.fraction * 100);

  // alwaysUp/alwaysDown indicate no rise/set event that day.
  // SunCalc may also return undefined rise/set without setting alwaysUp/Down.
  const hasRise = !moonTimes.alwaysUp && !moonTimes.alwaysDown && moonTimes.rise instanceof Date;
  const hasSet = !moonTimes.alwaysUp && !moonTimes.alwaysDown && moonTimes.set instanceof Date;
  const rise = hasRise ? moonTimes.rise : null;
  const set = hasSet ? moonTimes.set : null;

  return { phase, illumination, phaseValue, rise, set };
}

export function getCelestialData(date: Date, lat: number, lng: number): CelestialData {
  return {
    sun: getSunTimes(date, lat, lng),
    moon: getMoonData(date, lat, lng),
  };
}

export function formatCrepuscularWindow(dawn: Date, dusk: Date): string {
  const fmt = (d: Date) =>
    d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `${fmt(dawn)} – ${fmt(dusk)}`;
}
